type JsonObject = Record<string, unknown>;

export async function* handleSSEStream<T extends JsonObject>(
  streamingResponse: Response,
): AsyncGenerator<T, void, unknown> {
  const reader = streamingResponse.body?.getReader();
  if (!reader) {
    throw new Error("SSE response body is not readable");
  }

  const decoder = new TextDecoder();
  let buffer = "";

  const MAX_BUFFER_CHARS = 1_000_000;
  const MAX_EXTRACTED_OBJECTS = 50;
  const MAX_EXTRACT_LINE_CHARS = 100_000;

  while (true) {
    const rawChunk = await reader.read();
    const { done, value } = rawChunk;
    if (done) {
      break;
    }

    buffer += decoder.decode(value, { stream: true });
    if (buffer.length > MAX_BUFFER_CHARS) {
      throw new Error("SSE stream buffer exceeded limit");
    }

    const lines = buffer.split("\n");
    buffer = lines.pop() || "";

    for (const line of lines) {
      if (line.trim() === "") continue;

      try {
        const data = JSON.parse(line) as T;
        yield data;
      } catch (error) {
        console.error("Error parsing SSE data:", error);

        const jsonObjects =
          line.length <= MAX_EXTRACT_LINE_CHARS
            ? line.match(/\{[^{}]*\}/g)
            : null;
        if (jsonObjects) {
          for (const jsonObj of jsonObjects.slice(0, MAX_EXTRACTED_OBJECTS)) {
            try {
              const data = JSON.parse(jsonObj) as T;
              yield data;
            } catch (innerError) {
              console.error("Error parsing extracted JSON:", innerError);
            }
          }
        }
      }
    }
  }

  if (buffer.trim() !== "") {
    try {
      const data = JSON.parse(buffer) as T;
      yield data;
    } catch (error) {
      console.error("Error parsing remaining SSE buffer:", error);
    }
  }
}
